import dayjs, { type Dayjs } from 'dayjs'
import { DayjsFormats } from 'shared/lib/dayjs'
import { uiStore } from 'shared/store/uiStore'
import { QUIET_HOURS_ALLOWED_TIME_TO } from 'entities/QuietHours/lib/constants'

export const getLocalContactTimeFormatted = (tz: string | undefined): string => {
  if (!tz) return '--:-- --'
  try {
    return dayjs.utc().tz(tz).format(DayjsFormats.time2)
  } catch {
    return '--:-- --'
  }
}

const parseToMinutes = (key: string, defaultMinutes: number): number => {
  if (!key) return defaultMinutes
  const parts = key.split(':')
  const hours = parseInt(parts[0], 10)
  const minutes = parseInt(parts[1] ?? '0', 10)
  return Number.isNaN(hours) || Number.isNaN(minutes) ? defaultMinutes : hours * 60 + minutes
}

export const isInQuietHoursTimeframe = (
  tz: string | undefined,
  allowed_time_from: string,
  allowed_time_to: string
): boolean => {
  if (!tz) return false
  try {
    const now = dayjs.utc().tz(tz)
    const currentMinutes = now.hour() * 60 + now.minute()
    const fromMinutes = parseToMinutes(allowed_time_from, 9 * 60)
    const toMinutes = parseToMinutes(allowed_time_to, 20 * 60)

    if (fromMinutes <= toMinutes) {
      return currentMinutes < fromMinutes || currentMinutes > toMinutes
    }
    return currentMinutes > toMinutes && currentMinutes < fromMinutes
  } catch {
    return false
  }
}

export const normalizeTime = (time: string): string => {
  if (!time) return QUIET_HOURS_ALLOWED_TIME_TO
  if (time.split(':').length === 3) {
    const [hours, minutes] = time.split(':')
    return `${hours}:${minutes}`
  }
  if (time.split(':').length === 2) return time
  return QUIET_HOURS_ALLOWED_TIME_TO
}

export const formatTimeTo12Hour = (time: string): string => {
  if (!time) return '12:00 AM'
  const normalizedTime = normalizeTime(time)
  const today = uiStore.dayjsWithoutTimezone().format(DayjsFormats.dateSort)
  const timeObj = dayjs(`${today} ${normalizedTime}`, DayjsFormats.apiFormat3)
  return timeObj.format(DayjsFormats.time2)
}

export const openQuietHoursSettings = () => {
  window.open(`${window.location.origin}/settings/organization/settings/double-opt-in`, '_blank')
}

export const getQuietHoursAvailableTime = ({
  tz,
  send_at,
}: {
  tz: string
  send_at: Dayjs
}): string => {
  const effectiveTz = tz || dayjs.tz.guess()
  const todayInTZ = dayjs().tz(effectiveTz).format(DayjsFormats.dateSort)
  const sendAtDate = send_at.tz(effectiveTz).format(DayjsFormats.dateSort)
  const time = send_at.format(DayjsFormats.time2)

  if (sendAtDate === todayInTZ) return `Today, ${time}`
  return send_at.tz(effectiveTz).format(DayjsFormats.fullWithAt)
}

export type IGetNextQuietHoursSendAtParams = {
  contactTZ: string
  allowed_time_from: string
  allowed_time_to: string
  is_block_on_weekend: boolean
}

export const getNextQuietHoursSendAt = (params: IGetNextQuietHoursSendAtParams): Dayjs | null => {
  const { contactTZ, allowed_time_from, allowed_time_to, is_block_on_weekend } = params
  if (!contactTZ || !allowed_time_from || !allowed_time_to) return null

  const fromMinutes = parseToMinutes(allowed_time_from, 9 * 60)
  const toMinutes = parseToMinutes(allowed_time_to, 20 * 60)

  let value = dayjs().tz(contactTZ)
  const blockWeekend = is_block_on_weekend

  const moveToNextAllowedDay = (d: Dayjs): Dayjs => {
    let next = d
    if (blockWeekend) {
      while (next.day() === 0 || next.day() === 6) {
        next = next.add(1, 'day')
      }
    }
    return next
  }

  const setTime = (d: Dayjs, minutesSinceMidnight: number) => {
    const hours = Math.floor(minutesSinceMidnight / 60)
    const mins = minutesSinceMidnight % 60
    return d.startOf('day').add(hours, 'hour').add(mins, 'minute')
  }

  const sameDayWindow = fromMinutes <= toMinutes

  for (let safety = 0; safety < 14; safety++) {
    const day = value.day()
    if (blockWeekend && (day === 0 || day === 6)) {
      value = moveToNextAllowedDay(value.add(1, 'day')).startOf('day')
      continue
    }
    const currentMinutes = value.hour() * 60 + value.minute()

    if (sameDayWindow) {
      if (currentMinutes < fromMinutes) {
        return setTime(value, fromMinutes)
      }
      if (currentMinutes <= toMinutes) {
        const atMinute = value.startOf('minute')
        return atMinute.isBefore(value) ? value.add(1, 'minute').startOf('minute') : atMinute
      }
      value = moveToNextAllowedDay(value.add(1, 'day')).startOf('day')
      continue
    }

    if (currentMinutes >= fromMinutes || currentMinutes <= toMinutes) {
      const atMinute = value.startOf('minute')
      return atMinute.isBefore(value) ? value.add(1, 'minute').startOf('minute') : atMinute
    }
    return setTime(value, fromMinutes)
  }

  return null
}
